import {
  subscribeBithumbUsdtRate,
  getBithumbUsdtRateState
} from '@/services/bithumbRate'
import {
  normalizeFiatCurrencyCode,
  isKrwCurrency,
  isUsdCurrency
} from '@/utils/currencyCode'
import i18n from '@/utils/i18n'

const COINGECKO_PRICE_URL = 'https://api.coingecko.com/api/v3/simple/price'
const POLL_MS = 30000

const EMPTY_STATE = {
  rate: null,
  loading: false,
  error: '',
  updatedAt: null,
  connected: false,
  source: '',
  currency: ''
}

/** @type {Map<string, {
 *  state: typeof EMPTY_STATE,
 *  listeners: Set<Function>,
 *  pollTimer: number|null,
 *  fetchPromise: Promise<void>|null,
 *  bithumbUnsub: Function|null
 * }>} */
const channels = new Map()

function createChannel (currencyCode) {
  return {
    state: {
      ...EMPTY_STATE,
      currency: currencyCode,
      loading: !isUsdCurrency(currencyCode)
    },
    listeners: new Set(),
    pollTimer: null,
    fetchPromise: null,
    bithumbUnsub: null
  }
}

function getChannel (currencyCode) {
  const code = normalizeFiatCurrencyCode(currencyCode)
  if (!channels.has(code)) {
    channels.set(code, createChannel(code))
  }
  return channels.get(code)
}

function notify (channel) {
  const snapshot = { ...channel.state }
  channel.listeners.forEach(fn => {
    try {
      fn(snapshot)
    } catch (err) {
      // ignore listener errors
    }
  })
}

function rateErrorMessage (key) {
  try {
    return i18n.t(key)
  } catch (err) {
    return key
  }
}

function applyUsdRate (channel) {
  channel.state = {
    rate: 1,
    loading: false,
    error: '',
    updatedAt: Date.now(),
    connected: true,
    source: 'usd',
    currency: 'USD'
  }
  notify(channel)
}

async function fetchCoinGeckoUsdtRate (currencyCode) {
  const vs = String(currencyCode || '').toLowerCase()
  const url = `${COINGECKO_PRICE_URL}?ids=tether&vs_currencies=${encodeURIComponent(vs)}`
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) {
    throw new Error('rate_fetch_failed')
  }
  const body = await res.json()
  const price = Number(body && body.tether ? body.tether[vs] : NaN)
  if (!Number.isFinite(price) || price <= 0) {
    throw new Error('rate_unavailable')
  }
  return price
}

function refreshMarketRate (channel) {
  if (channel.fetchPromise) return channel.fetchPromise
  const code = channel.state.currency
  if (!channel.state.rate) {
    channel.state.loading = true
    channel.state.error = ''
    notify(channel)
  }

  channel.fetchPromise = fetchCoinGeckoUsdtRate(code)
    .then(price => {
      channel.state.rate = price
      channel.state.loading = false
      channel.state.error = ''
      channel.state.updatedAt = Date.now()
      channel.state.connected = true
      channel.state.source = 'market'
      notify(channel)
    })
    .catch(() => {
      channel.state.loading = false
      channel.state.connected = false
      if (!channel.state.rate) {
        channel.state.error = rateErrorMessage('wallet.rateConnectFail')
      } else {
        channel.state.error = rateErrorMessage('wallet.rateDisconnected')
      }
      notify(channel)
    })
    .finally(() => {
      channel.fetchPromise = null
    })

  return channel.fetchPromise
}

function startMarketPolling (channel) {
  if (channel.pollTimer) return
  refreshMarketRate(channel)
  channel.pollTimer = window.setInterval(() => {
    refreshMarketRate(channel)
  }, POLL_MS)
}

function stopMarketPolling (channel) {
  if (channel.pollTimer) {
    window.clearInterval(channel.pollTimer)
    channel.pollTimer = null
  }
}

function startBithumbBridge (channel) {
  if (channel.bithumbUnsub) return
  channel.bithumbUnsub = subscribeBithumbUsdtRate((snapshot) => {
    channel.state = {
      rate: snapshot.rate,
      loading: snapshot.loading,
      error: snapshot.error || '',
      updatedAt: snapshot.updatedAt,
      connected: snapshot.connected,
      source: 'bithumb',
      currency: 'KRW'
    }
    notify(channel)
  })
}

function stopBithumbBridge (channel) {
  if (channel.bithumbUnsub) {
    channel.bithumbUnsub()
    channel.bithumbUnsub = null
  }
}

function startChannel (channel) {
  const code = channel.state.currency
  if (isUsdCurrency(code)) {
    applyUsdRate(channel)
    return
  }
  if (isKrwCurrency(code)) {
    startBithumbBridge(channel)
    return
  }
  startMarketPolling(channel)
}

function stopChannel (channel) {
  stopMarketPolling(channel)
  stopBithumbBridge(channel)
}

/**
 * 사이트 화폐 기준 1 USDT = {rate} fiat 구독.
 * - KRW: 빗썸
 * - USD: 1:1
 * - 기타: CoinGecko 시세
 * @returns {function} unsubscribe
 */
export function subscribeUsdtFiatRate (currency, onChange) {
  const code = normalizeFiatCurrencyCode(currency)
  const channel = getChannel(code)
  channel.listeners.add(onChange)
  onChange({ ...channel.state })
  if (channel.listeners.size === 1) startChannel(channel)
  return () => {
    channel.listeners.delete(onChange)
    if (channel.listeners.size === 0) stopChannel(channel)
  }
}

export function getUsdtFiatRateState (currency) {
  const code = normalizeFiatCurrencyCode(currency)
  if (isUsdCurrency(code)) {
    return {
      rate: 1,
      loading: false,
      error: '',
      updatedAt: Date.now(),
      connected: true,
      source: 'usd',
      currency: 'USD'
    }
  }
  if (isKrwCurrency(code)) {
    const bithumb = getBithumbUsdtRateState()
    return {
      rate: bithumb.rate,
      loading: bithumb.loading,
      error: bithumb.error || '',
      updatedAt: bithumb.updatedAt,
      connected: bithumb.connected,
      source: 'bithumb',
      currency: 'KRW'
    }
  }
  const channel = channels.get(code)
  return channel ? { ...channel.state } : { ...EMPTY_STATE, currency: code, loading: true }
}
